/*******************************************************************************
 * Copyright (c) 2025 Bula Technologies
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 * Version: 1.0.0
 *
 *******************************************************************************/

import { useEffect } from 'react';
import { Grid } from '@mui/material';
import PageHeader from './PageHeader';
import PageFooter from './PageFooter';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui'; 
import { getUserInfo } from '../../store/reducers/user/userAction';
import { initializedSuccess } from '../../store/reducers/initialized/initializedSlice';
import { useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth } from '../../config/firebase/firebaseClient';

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { classes } = useStyles();

  const auth = getFirebaseAuth();  

  const initializeApp = useCallback(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        const userInfoAction = await dispatch(getUserInfo());
        const userInfo = userInfoAction.payload;

        if (user && userInfo && Object.keys(userInfo).length > 0) {
 
          // Redirect if on login/register/home
          if (
            location.pathname === "/" ||
            location.pathname === "/login" ||
            location.pathname.includes("/register")
          ) {
            navigate("/dashboard");
            dispatch(initializedSuccess());
          }
        } else {
          // Redirect to login if not on allowed pages
          const allowedPaths = [
            "/login",
            "/tos",
            "/privacypolicy",
            "/contact",
            "/about",
          ];
          if (!allowedPaths.includes(location.pathname) && !location.pathname.includes("/register")) {
            navigate("/login");
          }
        }
      } catch (error) {
        console.error(error);
        navigate("/login");
        localStorage.clear();
        window.location.reload(true);
      }
    });

    return () => unsubscribe();
  }, [auth, dispatch, location.pathname, navigate]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);


  return (
    <div className={classes.layout}  >
      <PageHeader />
      <main className={classes.main} >


        <Grid container justify="center">
          <Grid container className={classes.gridInner}>
            <Grid item xs={12} className={classes.maxWidthDiv}>
              {children}
            </Grid>
          </Grid>
        </Grid>
      </main>

      <PageFooter />

    </div>
  );
};
const useStyles = makeStyles({ "name": { Layout } })((theme) => ({
  layout: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,

  },
  main: {
    flex: 1,
    marginBottom: '10px',
  },
  appPanel: {
    position: 'fixed',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%) translateX(-100%)',
    width: '250px',  // Bigger width for the sidebar
    height: 'auto',  // Allow it to take full height
    padding: '16px 20px',  // Bigger padding
    backdropFilter: 'blur(10px)',
    background: `linear-gradient(180deg, ${theme.palette.primary.main}90, ${theme.palette.secondary.main}80)`, // Using theme primary and secondary colors
    color: '#fff',
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    boxShadow: '8px 0 20px rgba(0, 0, 0, 0.1)',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',  // Increased gap for better spacing
    animation: 'slideIn 0.4s ease forwards',
    transition: 'opacity 0.3s ease-in-out, transform 0.4s ease-in-out',
  },

  appOption: {
    padding: '18px 22px',  // Larger padding for options
    backgroundColor: 'rgba(255, 255, 255, 0.3)', // Slightly transparent
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '18px',  // Larger font size
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,  // Darker red when hovered
      transform: 'translateX(4px)',
      boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
    },
  },

  homeButton: {
    padding: '14px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '16px',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,  // Darker red when hovered
      transform: 'translateX(4px)',
      boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
    },
  },
  logosWrapper: {
    position: "relative",
    marginTop: "80px",
    display: "flex",
    justifyContent: "center",
    transform: "translate(0px, -30px)",
  },

  '@keyframes slideIn': {
    from: {
      transform: 'translateX(-100%)',
      opacity: 0,
    },
    to: {
      transform: 'translateX(0)',
      opacity: 1,
    },
  },
}));


export default Layout;